import { useState } from "react";
import { useLocation } from "react-router-dom";
import { SaveMediaDto } from "src/api/organization/org-types";
import { ApplicationFormModel, ProductModel } from "src/api/product/product-types";
import { BaseButton, BaseRadio } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import MarkdownEditor from "src/components/MarkdownEditor";
import Link from "src/components/PartnerLink";
import UsageApplicationFileUpload from "src/pages/contract/commonComponents/UsageApplicationFileUpload";
import { PagePath } from "src/pages/product/details";
import { ApplicationSummary, ProductApplication } from "src/pages/product/hooks/useUsageApplication";
import { useModal } from "src/recoil/modalState/hook";
import UsageApplicationContractorSection from "./UsageApplicationContractorSection";

type Props = {
  fileList: SaveMediaDto[];

  onClose: () => void;
  applicationFormData?: ProductApplication | null;
  product?: ProductModel;
  applicationSummary?: ApplicationSummary;
  isUseApplication?: boolean; // 이용신청서 사용 여부
  onSaveFormData: (data: ApplicationFormModel) => void;
};

const defaultApplicationForm = {
  isUseApplication: false,
  formData: {
    richText: "",
    attachmentConfig: {
      isRequired: false,
    },
  },
};

const UsageApplicationForm = ({
  fileList, //
  // setFileList,
  onClose,
  applicationFormData,
  applicationSummary,
  onSaveFormData,
}: Props) => {
  const { pathname } = useLocation();
  const isProductPage = pathname.includes("/product/");

  const { setBaseModal } = useModal();

  const [applicationForm, setApplicationForm] = useState<ProductApplication>(applicationFormData || defaultApplicationForm);

  const [attachments, setAttachments] = useState<SaveMediaDto[]>(fileList);

  return (
    <>
      <section className="base-abstract-modal__title flex-center-between border-bottom-gray ">
        <h1>이용신청서</h1>
      </section>

      <section className="base-abstract-modal__contents ">
        <article className="contents-container__wrap-article">
          {isProductPage ? (
            <div className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>사용 여부</p>
              </div>
              <div className="contents-container__grid-contents">
                <div>
                  <div className="flex-center">
                    <BaseRadio
                      id={"useApplication1"}
                      name={"useApplication"}
                      label={"미사용"}
                      onChange={(_: boolean) => {
                        setApplicationForm((prev) => ({
                          ...prev,
                          isUseApplication: false,
                        }));
                      }}
                      className="mr16"
                      checked={!applicationForm.isUseApplication || false}
                    />
                    <BaseRadio
                      id="useApplication2"
                      name="useApplication"
                      label="사용"
                      onChange={(_: boolean) => {
                        setApplicationForm((prev) => ({
                          ...prev,
                          isUseApplication: true,
                        }));
                      }}
                      className="mr16"
                      checked={applicationForm.isUseApplication || false}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <BaseSectionTitle title="상품정보" className="pb20" noHashLink />
                <div className="flex-center">
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p>상품명</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div>
                        <div className="flex-center">
                          <p className="mr10">{applicationSummary?.productInfo?.spaceProductName || "-"}</p>
                          <Link
                            target="_blank"
                            className="text-underline"
                            to={`${PagePath.product.detail.replace(":id", applicationSummary?.productInfo?.spaceProductId?.toString() || "")}`}
                          >{`(id:${applicationSummary?.productInfo?.spaceProductId?.toString() || ""})`}</Link>
                        </div>
                      </div>
                    </div>
                  </section>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p>공용공간</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <p>{applicationSummary?.productInfo?.facilityName || "-"}</p>
                    </div>
                  </section>
                </div>
                <div className="flex-center">
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p>신청기간</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <p>{applicationSummary?.productInfo?.useStartEndTime || "-"}</p>
                    </div>
                  </section>
                </div>
              </div>

              {/* 계약자 정보 */}
              {applicationSummary && <UsageApplicationContractorSection applicationSummary={applicationSummary} />}
            </>
          )}
          <div>
            {!isProductPage && <BaseSectionTitle title="이용 정보" className="py20" noHashLink />}

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>상세 내용</p>
              </div>
              <div className="contents-container__grid-contents contents-container__1200">
                <div className="minmax627">
                  <MarkdownEditor
                    value={applicationForm.formData.richText || ""}
                    onChange={(value?: string) => {
                      setApplicationForm((prev) => ({
                        ...prev,
                        formData: { ...prev.formData, richText: value ?? "" },
                      }));
                    }}
                    errorText={""}
                    height="410px"
                  />
                </div>
              </div>
            </section>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className={applicationForm.formData.attachmentConfig?.isRequired === true ? "required" : ""}>첨부파일</p>
              </div>

              <div className="contents-container__grid-contents contents-container__1200">
                {isProductPage ? (
                  <div>
                    <div className="flex-center-start">
                      <BaseRadio
                        id={"uploadFile1"}
                        name={"uploadFile"}
                        label={"선택"}
                        onChange={(_: boolean) => {
                          setApplicationForm((prev) => ({
                            ...prev,
                            formData: { ...prev.formData, attachmentConfig: { isRequired: false } },
                          }));
                        }}
                        className="mr16"
                        checked={!applicationForm.formData.attachmentConfig?.isRequired || false}
                      />
                      <BaseRadio
                        id="uploadFile2"
                        name="uploadFile"
                        label="필수"
                        onChange={(_: boolean) => {
                          setApplicationForm((prev) => ({
                            ...prev,
                            formData: { ...prev.formData, attachmentConfig: { isRequired: true } },
                          }));
                        }}
                        className="mr16"
                        checked={applicationForm.formData.attachmentConfig?.isRequired || false}
                      />
                    </div>
                  </div>
                ) : (
                  <UsageApplicationFileUpload
                    type="PROOF"
                    fileMaxLength={10}
                    images={attachments}
                    setImages={(files: SaveMediaDto[]) => {
                      setAttachments(files);
                      setApplicationForm((prev) => ({
                        ...prev,
                        formData: {
                          ...prev.formData,
                          attachments: files.map((file) => ({
                            ...file,
                            serviceType: "CT_APPLICATION",
                            serviceTag: "APPLICATION_ATTACHMENT",
                            isPrimary: false,
                          })),
                        },
                      }));
                    }}
                  />
                )}
              </div>
            </section>
          </div>
        </article>
      </section>
      <section className="base-abstract-modal__btn-wrap ">
        <div className="flex-center">
          <BaseButton
            title="취소"
            className="color-white mr10"
            onClick={() => {
              setBaseModal({
                isOpen: true,
                title: "작성을 취소하시겠습니까?",
                children: (
                  <div>
                    <p>확인을 누르면 저장되지 않습니다.</p>
                  </div>
                ),
                btnLeftTitle: "취소",
                btnRightTitle: "확인",
                onClick: () => {
                  setApplicationForm(applicationFormData!);
                  onClose();
                  setBaseModal({ isOpen: false });
                },
                onClose: () => {
                  setBaseModal({ isOpen: false });
                },
              });
            }}
          />
          <BaseButton
            title="완료"
            onClick={() => {
              if (
                applicationForm.formData.attachmentConfig?.isRequired &&
                (attachments.length === 0 || attachments.filter((item) => item.cmdType !== "D").length === 0)
              ) {
                console.log("공용이냐?");
                setBaseModal({
                  isOpen: true,
                  title: "첨부파일 등록은 필수입니다.",
                  onClick: () => {
                    setBaseModal({ isOpen: false });
                  },
                  btnRightTitle: "확인",
                });
                return;
              }
              onSaveFormData({ ...applicationForm.formData, attachments: attachments });
            }}
          />
        </div>
      </section>
    </>
  );
};

export default UsageApplicationForm;
